/**
 * Whether the refresh token on this device is guarded by the CURRENTLY ENROLLED
 * biometric set, and how to read the OS's answer when a keystore read fails.
 *
 * Guarded means: somebody who adds their own face or finger to a stolen,
 * unlocked handset finds that the next app open demands a full PanLuma login
 * rather than handing them the session. Without it the biometric app lock is a
 * JS-side gate over a credential the OS will give to anyone who asks.
 *
 * **How, and why not the obvious way.** The brief asks for an access control on
 * the token's own keystore item — iOS `SecAccessControl(.biometryCurrentSet)`,
 * Android `setInvalidatedByBiometricEnrollment`. Measured on an API 36 image,
 * the Android half of that does not work as written: a key is invalidated by
 * enrolment only when it demands authentication for EVERY use and accepts
 * biometrics alone, which on the token's own key would mean a second prompt at
 * every unlock and no device-passcode fallback at all. The same behaviour is
 * bought instead by `biometrySetGuard`, a canary key that answers "has the set
 * changed?" silently. The measurements, and the security property this trades
 * away, are in `docs/platform/mobile-app.md` § Biometry-bound refresh token.
 *
 * A **leaf**, like `secureTokenStore` and for the same reason: it may import the
 * platform flag, the guard, and the app-lock policy CACHE (itself a leaf over
 * localStorage), and nothing else. `secureTokenStore` depends on this module,
 * and `axiosInstance` and `AuthContext` depend on that.
 */
import { isCapacitor } from '../api/config';
import { readCachedPolicy } from '../applock/appLockState';
import { isGuardKnownUnavailable } from './biometrySetGuard';

/**
 * Should a token written NOW be bound to the enrolled biometric set?
 *
 * Only when the app lock is on. The guard is consulted at exactly one moment —
 * the unlock — so on a device whose owner has the lock switched off there is no
 * moment at which to consult it, and a changed set would go unnoticed by never
 * being looked for. Users with the lock off keep the plain keystore item and
 * today's silent cold-start refresh; the hardening is a property of the lock,
 * which is also what makes switching the lock off a re-store rather than a
 * sign-out.
 *
 * Read from the CACHED policy, not the server: this is consulted on an offline
 * cold start, and `appLockState` caches for exactly that reason.
 */
export function shouldBindRefreshToken(): boolean {
  if (!isCapacitor) return false;
  // A device with no biometry enrolled, or a binary with no native guard, has no
  // set to watch. Sticky, so the policy sync does not re-store the token on every
  // refetch asking for a guard it has already been told it cannot have.
  if (isGuardKnownUnavailable()) return false;
  const policy = readCachedPolicy();
  return policy !== null && policy.enabled;
}

/**
 * What a failed keystore operation means for the session.
 *
 * - `invalidated` — the key is permanently gone because the enrolled set
 *   changed. The stored token can never be read again, so the session on this
 *   device is over and the person completes a full login.
 * - `transient` — the device could not answer right now: a locked Keychain, a
 *   keystore that is not ready, an OS hiccup. The session is FINE and must
 *   survive, which is the whole reason `SecureTokenStoreError` exists.
 */
export type StoreFailureKind = 'invalidated' | 'transient';

/**
 * Android's rejection carries the OS exception's CLASS NAME, interpolated into
 * the message by `KeyStoreException.init` ("An OS error occurred (%s)"), because
 * every OS failure collapses into the single code `osError`. So the class name
 * in the message is the only discriminator the plugin offers.
 *
 * Neither is expected on the token's OWN key, which is not biometry-bound — the
 * canary carries that duty. They are kept because a keystore can still lose key
 * material for its own reasons: `UnrecoverableKeyException` reaches
 * `decryptString` from `keyStore.getEntry` when the material is no longer usable,
 * and `getSecretKey` catches it on the WRITE path and mints a fresh key, so
 * seeing it on a READ means the stored ciphertext is orphaned and equally
 * unrecoverable. Either way the token is gone and the session on this device is
 * over.
 */
const ANDROID_INVALIDATED_EXCEPTIONS = [
  'KeyPermanentlyInvalidatedException',
  'UnrecoverableKeyException'
];

/**
 * iOS collapses every OS failure into `osError` too, with the numeric `OSStatus`
 * interpolated into the message ("An OS error occurred (%d)").
 *
 * Only `errSecAuthFailed` is read as invalidation. The neighbouring codes are
 * deliberately NOT here and each would be a sign-out bug:
 * `errSecInteractionNotAllowed` (-25308) is a Keychain locked by the device
 * being locked, and `errSecNotAvailable` (-25291) is one that has not become
 * available yet — both are the offline-cold-start case the session must
 * survive. `errSecItemNotFound` (-25300) is not a failure at all; the read
 * resolves null and the caller reads that as "no token".
 */
const IOS_INVALIDATED_STATUSES = new Set([
  -25293 // errSecAuthFailed
]);

const OS_STATUS_PATTERN = /An OS error occurred \((-?\d+)\)/;

function messageOf(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return '';
}

/**
 * Classify a keystore rejection.
 *
 * Unknown shapes are `transient` on purpose. The two mistakes are not
 * symmetrical: calling a real invalidation transient costs one wasted refresh
 * attempt and the person is asked to log in on the next launch anyway, while
 * calling a device fault an invalidation signs somebody out of their offline
 * data over a Keychain that was merely busy.
 */
export function classifyStoreFailure(error: unknown): StoreFailureKind {
  const message = messageOf(error);
  if (ANDROID_INVALIDATED_EXCEPTIONS.some((name) => message.includes(name))) {
    return 'invalidated';
  }
  const match = OS_STATUS_PATTERN.exec(message);
  if (match && IOS_INVALIDATED_STATUSES.has(Number(match[1]))) {
    return 'invalidated';
  }
  return 'transient';
}

/**
 * How the token CURRENTLY in the keystore was written.
 *
 * The read path obeys this marker rather than re-asking `shouldBindRefreshToken`,
 * because the two can legitimately disagree: turning the app lock off changes the
 * policy immediately, while the stored item stays bound until it is re-written.
 * A read that trusted the live policy would take the plain path against a bound
 * item — which throws on Android and, worse, resolves null on iOS. Write decides,
 * this records, read obeys.
 *
 * Absent means `plain`: every token written before this shipped is unbound, and
 * that is also the safe direction — the plain path never needs an unlock that
 * might not come.
 */
export type BindingMode = 'bound' | 'plain';

/**
 * Deliberately not in `storageKeys.ts`, which is the canonical home of the auth
 * SESSION keys, and deliberately not spelled with the refresh token's key inside
 * it: `tools/check_auth_storage_keys.py` matches those eight literals as a
 * SUBSTRING and has no escape marker, so a name merely containing one fails the
 * build.
 */
const BINDING_MARKER_KEY = 'panluma_token_binding_mode';

export function readStoredBindingMode(): BindingMode {
  return localStorage.getItem(BINDING_MARKER_KEY) === 'bound' ? 'bound' : 'plain';
}

export function writeStoredBindingMode(mode: BindingMode): void {
  if (mode === 'bound') {
    localStorage.setItem(BINDING_MARKER_KEY, mode);
    return;
  }
  localStorage.removeItem(BINDING_MARKER_KEY);
}

export function clearStoredBindingMode(): void {
  localStorage.removeItem(BINDING_MARKER_KEY);
}
